import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Avatar,
  Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, TableSortLabel,
  Chip,
  Fab,
  Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessIcon from "@mui/icons-material/Business";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEntidade } from "../../Hooks/EntidadeHook";
import { api_url } from "../../Utils/Urls";
import axios from "axios";
import { PdfView } from "../../Components/PdfView";
import { Loader } from "../../Components/Loader";

type TipoPesquisa = "cpf" | "cnpj" | "codigo";

// ---- Modelo da linha da tabela de serviços ----
export type ServicoRow = {
  ano: number;        // ex.: 2025
  mes: number;        // 1..12 (Jan=1)
  codigo: string;     // ex.: "2186"
  path: string;       // caminho completo para abrir no viewer
  nome: string;       // ex.: "2025_08_2186_02855867002.PDF"
  cpfCnpj?: string;   // ex.: "02855867002" (opcional)
};

const meses = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];

function mesPorExtenso(m: number) {
  return meses[m - 1] ?? String(m).padStart(2, "0");
}

function onlyDigits(v: string) {
  return (v || "").replace(/\D+/g, "");
}
function maskCPF(v: string) {
  const s = onlyDigits(v).slice(0, 11);
  return s
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
}
function maskCNPJ(v: string) {
  const s = onlyDigits(v).slice(0, 14);
  return s
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
}

// pega o último segmento do caminho (Windows ou Unix)
function basename(fullPath: string): string {
  const parts = fullPath.split(/[\\/]/);
  return parts[parts.length - 1] || fullPath;
}

/**
 * Converte um caminho de arquivo no formato:
 *   AAAA_MM_CODIGO_CPFORCNPJ.PDF
 * em uma linha da tabela (ServicoRow).
 * Retorna null se não casar o padrão.
 */
export function parseServicoPath(path: string): ServicoRow | null {
  const nome = basename(path);
  const re = /^(\d{4})_(\d{2})_([^_]+)_([^.]+)\.pdf$/i;
  const m = re.exec(nome);
  if (!m) return null;

  const ano = Number(m[1]);
  const mes = Number(m[2]);
  const codigo = m[3];
  const cpfCnpj = m[4];
  if (mes < 1 || mes > 12) return null;

  return { ano, mes, codigo, path, nome, cpfCnpj };
}

export function parseServicoPaths(paths: string[]): ServicoRow[] {
  const unique = Array.from(new Set(paths));
  return unique.map(parseServicoPath).filter((r): r is ServicoRow => r !== null);
}

export async function buscaArquivo(caminho: string, pasta: string): Promise<string[]> {
  try {
    const { data } = await axios.get<string[]>(caminho, { timeout: 15000 });
    if (!Array.isArray(data) || data.length === 0 || typeof data[0] !== "string") return [];
    return data.map((d) => "E:\\sistemas\\unico\\" + pasta + "\\guias_servicos\\" + d);
  } catch (error) {
    console.error("Erro ao verificar arquivo:", error);
    return [];
  }
}

export const Servicos: React.FC = () => {
  const { entidade } = useEntidade();
  const now = new Date();
  const anoAtual = now.getFullYear();
  const mesAtual = now.getMonth() + 1; // 1..12

  // — estado de "tela": busca (form) ou resultados
  const [mostrarBusca, setMostrarBusca] = useState(true);

  const [tipo, setTipo] = useState<TipoPesquisa>("cpf");
  const [valor, setValor] = useState("");

  const TODOS = "TODOS" as const;
  type MesValue = number | typeof TODOS;
  type AnoValue = number | typeof TODOS;

  const [ano, setAno] = useState<AnoValue>("TODOS");
  const [mes, setMes] = useState<MesValue>("TODOS");
  const [erroValor, setErroValor] = useState<string | null>(null);

  const [visualizarPdfOpen, setVisualizarPdfOpen] = useState(false);
  const [nomesArquivosPdf, setNomesArquivosPdf] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ServicoRow[]>([]);

  type Order = "asc" | "desc";
  type OrderBy = "ano" | "mes" | "codigo";

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<OrderBy>("ano");

  const handleRequestSort = (property: OrderBy) => {
    setOrder((prev) => (orderBy === property && prev === "asc" ? "desc" : "asc"));
    setOrderBy(property);
  };

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (orderBy) {
        case "ano": cmp = a.ano - b.ano; break;
        case "mes": cmp = a.mes - b.mes; break;
        case "codigo": cmp = a.codigo.localeCompare(b.codigo, "pt-BR", { numeric: true }); break;
      }
      return order === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [rows, order, orderBy]);

  const anosDisponiveis = useMemo(() => {
    const base = now.getFullYear();
    return [base - 1, base, base + 1];
  }, [now]);

  const mesesOptions = useMemo(
    () => meses.map((label, idx) => ({ label, value: idx + 1 })),
    []
  );

  const mesesFiltrados = useMemo(() => {
    if (ano !== anoAtual) return mesesOptions;
    return mesesOptions.filter((opt) => opt.value < mesAtual);
  }, [ano, anoAtual, mesAtual, mesesOptions]);

  useEffect(() => {
    if (mes === TODOS) return;
    const permitidos = new Set(mesesFiltrados.map(o => o.value));
    if (!permitidos.has(mes)) {
      const fallback = mesesFiltrados.at(-1)?.value ?? 1;
      setMes(fallback);
    }
  }, [ano, mesesFiltrados, mes]);

  const iconeCampo =
    tipo === "cpf" ? <BadgeIcon /> : tipo === "cnpj" ? <BusinessIcon /> : <QrCodeIcon />;

  const labelCampo =
    tipo === "cpf" ? "CPF do titular" : tipo === "cnpj" ? "CNPJ do titular" : "Código do Carnê";

  const placeholderCampo =
    tipo === "cpf" ? "000.000.000-00" : tipo === "cnpj" ? "00.000.000/0000-00" : "Ex.: 123456";

  const valorFormatado =
    tipo === "cpf" ? maskCPF(valor) : tipo === "cnpj" ? maskCNPJ(valor) : valor;

  function validar() {
    const v = onlyDigits(valor);
    if (tipo === "cpf") {
      if (v.length !== 11) return "Informe um CPF com 11 dígitos.";
    } else if (tipo === "cnpj") {
      if (v.length !== 14) return "Informe um CNPJ com 14 dígitos.";
    } else {
      if (!v || v.length < 3) return "Informe um código de carnê válido.";
    }
    return null;
  }

  function handleChangeTipo(_e: React.MouseEvent<HTMLElement>, novo: TipoPesquisa | null) {
    if (!novo) return;
    setTipo(novo);
    setValor("");
    setErroValor(null);
  }

  const handleSubmit = async () => {
    const err = validar();
    setErroValor(err);
    if (err) return;

    setLoading(true);
    try {
      const payload = {
        tipo,
        valor: onlyDigits(valor),
        mes,
        ano,
        id_entidade: entidade?.id ?? undefined,
        banco: entidade?.banco ?? undefined,
      };
      const mesFormatado = mes === TODOS ? "*" : String(payload.mes).padStart(2, "0");
      const anoFormatado = ano === TODOS ? "*" : String(payload.ano);

      let arquivos: string[] = [];
      if (payload.tipo === "cpf" || payload.tipo === "cnpj") {
        const caminho = api_url + "Arquivo/pesquisar/?pasta=E:\\sistemas\\unico\\" + entidade.pasta_unico +
          "\\guias_servicos&padrao=*" + anoFormatado + "_" + mesFormatado + "*_" + payload.valor + ".pdf";
        arquivos = await buscaArquivo(caminho, entidade.pasta_unico);
      } else {
        const caminho = api_url + "Arquivo/pesquisar/?pasta=E:\\sistemas\\unico\\" + entidade.pasta_unico +
          "\\guias_servicos&padrao=*" + anoFormatado + "_" + mesFormatado + "*_" + payload.valor + "_*";
        arquivos = await buscaArquivo(caminho, entidade.pasta_unico);
      }

      if (arquivos.length > 0) {
        const parsed = parseServicoPaths(arquivos);
        setRows(parsed);
        setMostrarBusca(false); // ← foca na área de resultados
      } else {
        setRows([]);
        alert("Não foi possível encontrar documentos.");
      }
    } catch (e) {
      console.error("Erro ao buscar arquivo(s):", e);
      alert("Erro ao buscar arquivo(s).");
    } finally {
      setLoading(false);
    }
  };

  const handleVisualizarTodos = () => {
    if (sortedRows.length === 0) return;
    setNomesArquivosPdf(sortedRows.map(r => r.path));
    setVisualizarPdfOpen(true);
  };

  const handleVisualizarUm = (path: string) => {
    setNomesArquivosPdf([path]);
    setVisualizarPdfOpen(true);
  };

  function onEnter(e: React.KeyboardEvent) {
    if (e.key === "Enter") handleSubmit();
  }

  // ——— UI ———
  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          p: 2,
          borderRadius: 3,
          bgcolor: "background.paper",
          boxShadow: (t) => t.shadows[1],
        }}
      >
        <Avatar
          variant="rounded"
          src={entidade?.logo || undefined}
          alt={entidade?.titulo || "Entidade"}
          sx={{ width: 56, height: 56, borderRadius: 2 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap fontWeight={700}>
            {entidade?.titulo ?? "Entidade"}
          </Typography>
          {(entidade?.site || entidade?.portal || entidade?.site2) ? (
            <Typography
              variant="body2"
              color="primary"
              component="a"
              href={entidade?.site || entidade?.portal || entidade?.site2}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              {entidade?.site || entidade?.portal || entidade?.site2}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" noWrap>
              Serviços
            </Typography>
          )}
        </Box>
      </Box>

      {/* Card de busca — só aparece quando mostrarBusca = true */}
      {mostrarBusca && (
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardHeader
            title="Pesquisar Guias de Serviço"
            subheader="Busque por CPF, CNPJ ou Código do Carnê e selecione o período"
            sx={{ "& .MuiCardHeader-title": { fontWeight: 700 } }}
            // Se já houver resultados, mostra um Cancelar para voltar aos resultados
            action={rows.length > 0 ? (
              <Button onClick={() => setMostrarBusca(false)} sx={{ textTransform: "none" }}>
                Cancelar
              </Button>
            ) : null}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              {/* Botões de seleção */}
              <ToggleButtonGroup
                value={tipo}
                exclusive
                onChange={handleChangeTipo}
                color="primary"
                sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, display: "flex", flexWrap: "wrap", gap: 1 }}
              >
                <ToggleButton value="cpf" sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: 140, height: 56, fontSize: "1rem", fontWeight: 600, borderRadius: 2, textTransform: "none" }}>
                  CPF
                </ToggleButton>
                <ToggleButton value="cnpj" sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: 140, height: 56, fontSize: "1rem", fontWeight: 600, borderRadius: 2, textTransform: "none" }}>
                  CNPJ
                </ToggleButton>
                <ToggleButton value="codigo" sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: 180, height: 56, fontSize: "1rem", fontWeight: 600, borderRadius: 2, textTransform: "none" }}>
                  Código do Carnê
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Linha de filtros */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "flex-end" }}>
                <TextField
                  fullWidth
                  label={labelCampo}
                  value={valorFormatado}
                  onChange={(e) => { setErroValor(null); setValor(e.target.value); }}
                  onKeyDown={onEnter}
                  placeholder={placeholderCampo}
                  error={!!erroValor}
                  helperText={erroValor || " "}
                  InputProps={{ startAdornment: <InputAdornment position="start">{iconeCampo}</InputAdornment> }}
                  sx={{ height: 56 }}
                />

                <TextField
                  select
                  label="Mês"
                  value={mes}
                  onChange={(e) => { setMes(e.target.value === TODOS ? TODOS : Number(e.target.value)); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthIcon /></InputAdornment> }}
                  sx={{
                    minWidth: { xs: "100%", sm: 180 }, height: 56, display: "flex",
                    "& .MuiSelect-select": { display: "flex", alignItems: "center", height: "100%", paddingY: "14px" },
                  }}
                >
                  <MenuItem value={TODOS}>TODOS</MenuItem>
                  {mesesFiltrados.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Ano"
                  value={ano}
                  onChange={(e) => { const v = e.target.value; setAno(v === TODOS ? TODOS : Number(v)); }}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 }, height: 56, display: "flex",
                    "& .MuiSelect-select": { display: "flex", alignItems: "center", height: "100%", paddingY: "14px" },
                  }}
                >
                  <MenuItem value={TODOS}>TODOS</MenuItem>
                  {anosDisponiveis.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SearchIcon />}
                  onClick={handleSubmit}
                  sx={{ minWidth: { xs: "100%", sm: 180 }, height: 56, borderRadius: 2, fontWeight: 700 }}
                >
                  Pesquisar
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Dica: você pode digitar e pressionar <b>Enter</b> para pesquisar.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Resultados — só aparece quando mostrarBusca = false */}
      {!mostrarBusca && (
        <Box mt={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" rowGap={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>
                Resultados da busca
              </Typography>
              <Chip size="small" label={`${sortedRows.length} ${sortedRows.length === 1 ? "resultado" : "resultados"}`} />
            </Stack>

            <Stack direction="row" spacing={1} >
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setMostrarBusca(true)}
                sx={{ textTransform: "none", borderRadius: 2 }}
                color="secondary"
              >
                Nova pesquisa
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={<VisibilityIcon />}
                onClick={handleVisualizarTodos}
                disabled={rows.length === 0}
                sx={{ borderRadius: 2, fontWeight: 700, px: 2.5, boxShadow: (t) => t.shadows[2], textTransform: "none" }}
              >
                Visualizar todos ({sortedRows.length})
              </Button>
            </Stack>
          </Stack>

          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              Nenhum resultado para os filtros informados.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                overflow: "auto",
                "& thead th": {
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  fontSize: ".78rem",
                  color: "text.secondary",
                },
                "& td, & th": { fontVariantNumeric: "tabular-nums" },
                "& tbody tr:nth-of-type(odd)": { bgcolor: "action.hover" },
              }}
            >
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sortDirection={orderBy === "ano" ? order : false}>
                      <TableSortLabel
                        active={orderBy === "ano"}
                        direction={orderBy === "ano" ? order : "asc"}
                        onClick={() => handleRequestSort("ano")}
                      >
                        Ano
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="center" sortDirection={orderBy === "mes" ? order : false}>
                      <TableSortLabel
                        active={orderBy === "mes"}
                        direction={orderBy === "mes" ? order : "asc"}
                        onClick={() => handleRequestSort("mes")}
                      >
                        Mês
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="center" sortDirection={orderBy === "codigo" ? order : false}>
                      <TableSortLabel
                        active={orderBy === "codigo"}
                        direction={orderBy === "codigo" ? order : "asc"}
                        onClick={() => handleRequestSort("codigo")}
                      >
                        Código
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="center"><b>Visualizar</b></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedRows.map((r) => (
                    <TableRow key={r.path} hover>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight={600}>{r.ano}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1">{mesPorExtenso(r.mes)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {r.codigo}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button variant="text" onClick={() => handleVisualizarUm(r.path)}>
                          Visualizar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* FAB “Nova pesquisa” — só no mobile e só quando estamos nos resultados */}
      {!mostrarBusca && rows.length > 0 && (
        <Tooltip title="Nova pesquisa">
          <Fab
            color="default"
            onClick={() => setMostrarBusca(true)}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              display: { xs: "flex", md: "none" },
              zIndex: (t) => t.zIndex.tooltip + 1
            }}
          >
            <SearchIcon />
          </Fab>
        </Tooltip>
      )}

      <PdfView
        open={visualizarPdfOpen}
        onClose={() => setVisualizarPdfOpen(false)}
        nomeArquivo={nomesArquivosPdf}
        modoImpressao={true}
      />
      <Loader loading={loading} />
    </Box>
  );
};
